// Google Maps wrapper used by the itinerary and share views: geocodes idea
// areas per destination, draws numbered day markers with category colors,
// and an info window per stop. Requires GOOGLE_MAPS_API_KEY (utils/firebase).
import React from 'react';
import { useJsApiLoader, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../utils/firebase';
import { PALETTE } from '../theme';

const DAY_COLORS = [
  '#E85D4A', '#3A8DDE', '#2EAD85', '#F0A500',
  '#8B5CF6', '#EC4899', '#0EA5E9', '#F97316',
  '#10B981', '#6366F1',
];
const PARKING_COLOR = '#9CA3AF';
const GEO_CACHE_KEY = 'tp_geo_v1';
const MAP_LIBRARIES = [];

function readCache() {
  try { return JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || '{}'); } catch { return {}; }
}
function writeCache(c) {
  try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(c)); } catch {}
}
function geoKey(title, area, destination) {
  return `${title}|${area || ''}|${destination}`.toLowerCase();
}
function geocodeOne(geocoder, query) {
  return new Promise((res) => {
    geocoder.geocode({ address: query }, (results, status) => {
      res(status === 'OK'
        ? { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() }
        : null);
    });
  });
}
async function geocodeItem(geocoder, title, area, destination) {
  const queries = [
    area ? `${title}, ${area}, ${destination}` : `${title}, ${destination}`,
    area ? `${area}, ${destination}` : null,
  ].filter(Boolean);
  for (const q of queries) {
    const r = await geocodeOne(geocoder, q);
    if (r) return r;
  }
  return null;
}

function makePinIcon(color) {
  const svg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">` +
    `<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="${color}"/>` +
    `<circle cx="12" cy="12" r="5" fill="white" opacity="0.9"/>` +
    `</svg>`
  );
  return {
    url: `data:image/svg+xml,${svg}`,
    scaledSize: new window.google.maps.Size(24, 36),
    anchor: new window.google.maps.Point(12, 36),
  };
}

function fmtTime(h) {
  if (h === null || h === undefined) return '';
  const hr = Math.floor(h);
  const min = Math.round((h - hr) * 60);
  const ampm = hr >= 12 ? 'pm' : 'am';
  const h12 = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
  return `${h12}${min ? ':' + String(min).padStart(2, '0') : ''}${ampm}`;
}

const BTN = {
  appearance: 'none', border: 0, cursor: 'pointer', width: '100%',
  textAlign: 'left', padding: '7px 10px', background: 'transparent',
  fontFamily: 'Inter', fontSize: 12, color: '#221F1A',
  borderRadius: 6, display: 'block',
};

function MapContent({
  displayDays, displayOverflow, allIdeas, destination, timeOverrides,
  onEdit, onMoveToDay, onMoveToParking, onRemove, onClose,
}) {
  const [geoPos, setGeoPos] = React.useState(() => readCache());
  const [center, setCenter] = React.useState({ lat: 48.8566, lng: 2.3522 });
  const [geocoding, setGeocoding] = React.useState(true);
  const [totalToGeocode, setTotalToGeocode] = React.useState(0);
  const [geocodedCount, setGeocodedCount] = React.useState(0);
  const [selected, setSelected] = React.useState(null);
  const [moveMenuOpen, setMoveMenuOpen] = React.useState(false);
  const mapRef = React.useRef(null);

  // Reactive markers: recomputed whenever displayDays/overflow/geoPos change
  const markers = React.useMemo(() => {
    const result = [];
    displayDays.forEach((day, dayIdx) => {
      day.items.forEach(slot => {
        const k = geoKey(slot.idea.title, slot.idea.area || '', destination);
        const pos = geoPos[k];
        if (!pos) return;
        result.push({
          id: slot.idea.id,
          idea: slot.idea,
          dayIdx,
          dayLabel: day.date,
          start: timeOverrides?.[slot.idea.id] ?? slot.start,
          isParking: false,
          pos,
        });
      });
    });
    displayOverflow.forEach(ideaId => {
      const idea = allIdeas.find(x => x.id === ideaId);
      if (!idea) return;
      const k = geoKey(idea.title, idea.area || '', destination);
      const pos = geoPos[k];
      if (!pos) return;
      result.push({
        id: idea.id,
        idea,
        dayIdx: -1,
        dayLabel: 'Parking lot',
        start: null,
        isParking: true,
        pos,
      });
    });
    return result;
  }, [displayDays, displayOverflow, allIdeas, geoPos, destination, timeOverrides]);

  // Keep selected in sync when displayDays changes (e.g. after a move)
  React.useEffect(() => {
    if (!selected) return;
    for (let di = 0; di < displayDays.length; di++) {
      const slot = displayDays[di].items.find(s => s.idea.id === selected.id);
      if (slot) {
        setSelected(prev => ({
          ...prev,
          dayIdx: di,
          dayLabel: displayDays[di].date,
          start: timeOverrides?.[slot.idea.id] ?? slot.start,
          isParking: false,
        }));
        return;
      }
    }
    if (displayOverflow.includes(selected.id)) {
      setSelected(prev => ({ ...prev, dayIdx: -1, dayLabel: 'Parking lot', isParking: true }));
    } else {
      setSelected(null); // removed
    }
  }, [displayDays, displayOverflow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close move menu when selection changes
  React.useEffect(() => { setMoveMenuOpen(false); }, [selected]);

  // One-time geocoding on mount
  React.useEffect(() => {
    const geocoder = new window.google.maps.Geocoder();
    const cache = readCache();

    async function run() {
      const allItems = [];
      displayDays.forEach(day =>
        day.items.forEach(slot =>
          allItems.push({ title: slot.idea.title, area: slot.idea.area || '' })
        )
      );
      displayOverflow.forEach(id => {
        const idea = allIdeas.find(x => x.id === id);
        if (idea) allItems.push({ title: idea.title, area: idea.area || '' });
      });

      const seen = new Set();
      const toGeocode = [];
      for (const item of allItems) {
        const k = geoKey(item.title, item.area, destination);
        if (!cache[k] && !seen.has(k)) { seen.add(k); toGeocode.push(item); }
      }

      const destKey = geoKey('__dest__', '', destination);
      if (!cache[destKey]) {
        const pos = await geocodeOne(geocoder, destination);
        if (pos) cache[destKey] = pos;
      }
      if (cache[destKey]) setCenter(cache[destKey]);

      if (toGeocode.length === 0) {
        setGeocoding(false);
        setGeoPos({ ...cache });
        setTimeout(() => {
          if (mapRef.current && Object.keys(cache).length > 1) {
            const bounds = new window.google.maps.LatLngBounds();
            Object.entries(cache).forEach(([k, pos]) => { if (!k.startsWith('__dest__')) bounds.extend(pos); });
            mapRef.current.fitBounds(bounds, 60);
          }
        }, 100);
        return;
      }

      setTotalToGeocode(toGeocode.length);
      setGeocoding(true);
      for (const item of toGeocode) {
        const k = geoKey(item.title, item.area, destination);
        const pos = await geocodeItem(geocoder, item.title, item.area, destination);
        if (pos) { cache[k] = pos; setGeocodedCount(c => c + 1); setGeoPos({ ...cache }); }
      }
      writeCache(cache);
      setGeocoding(false);

      if (mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        let hasPoints = false;
        displayDays.forEach(day => day.items.forEach(slot => {
          const k = geoKey(slot.idea.title, slot.idea.area || '', destination);
          if (cache[k]) { bounds.extend(cache[k]); hasPoints = true; }
        }));
        if (hasPoints) mapRef.current.fitBounds(bounds, 60);
      }
    }

    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedColor = selected
    ? (selected.isParking ? PARKING_COLOR : DAY_COLORS[selected.dayIdx % DAY_COLORS.length])
    : '#ccc';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
        background: 'rgba(244,239,230,0.96)', backdropFilter: 'blur(8px)',
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `0.5px solid ${PALETTE.rule}`,
      }}>
        <div style={{ fontFamily: 'Fraunces', fontSize: 18, letterSpacing: -0.3, color: PALETTE.ink }}>Map view</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {geocoding && totalToGeocode > 0 && (
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.5 }}>
              {geocodedCount}/{totalToGeocode} located…
            </span>
          )}
          <button onClick={onClose} style={{
            appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: PALETTE.card,
            color: PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 13, cursor: 'pointer',
            padding: '6px 14px', borderRadius: 999,
          }}>Close</button>
        </div>
      </div>

      {/* Map */}
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        zoom={13}
        center={center}
        onLoad={map => { mapRef.current = map; }}
        onClick={() => setSelected(null)}
        options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
      >
        {markers.map((m, i) => {
          const color = m.isParking ? PARKING_COLOR : DAY_COLORS[m.dayIdx % DAY_COLORS.length];
          const isSelected = selected?.id === m.id && selected?.dayIdx === m.dayIdx;
          return (
            <Marker
              key={`${m.id}-${i}`}
              position={m.pos}
              icon={makePinIcon(color)}
              zIndex={isSelected ? 10 : 1}
              onClick={(e) => { e.stop?.(); setSelected(m); }}
            />
          );
        })}

        {selected && (
          <InfoWindow
            key={`iw-${selected.id}-${selected.dayIdx}`}
            position={selected.pos}
            onCloseClick={() => setSelected(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -36), disableAutoPan: false }}
          >
            <div style={{ fontFamily: 'Inter', width: 200 }}>
              {/* Title + area */}
              <div style={{ fontFamily: 'Fraunces', fontSize: 15, letterSpacing: -0.2, color: '#221F1A', lineHeight: 1.2, marginBottom: 3 }}>
                {selected.idea.title}
              </div>
              {selected.idea.area && (
                <div style={{ fontSize: 11, color: '#8A847B', marginBottom: 6 }}>{selected.idea.area}</div>
              )}

              {/* Day + time */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 11,
                  fontFamily: 'JetBrains Mono', fontWeight: 600, letterSpacing: 0.3,
                  background: `${selectedColor}22`, color: selectedColor,
                }}>
                  {selected.isParking ? 'Parking lot' : `Day ${selected.dayIdx + 1} · ${selected.dayLabel}`}
                </span>
                {!selected.isParking && selected.start !== null && (
                  <span style={{ fontSize: 11, color: '#54504A', fontFamily: 'JetBrains Mono' }}>
                    {fmtTime(selected.start)}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div style={{ borderTop: '1px solid #e5decf', paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <button
                  style={BTN}
                  onMouseEnter={e => e.currentTarget.style.background = '#f4efe6'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => { onEdit?.(selected.idea); setSelected(null); }}
                >
                  ✏️ Edit details
                </button>

                <button
                  style={{ ...BTN, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f4efe6'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setMoveMenuOpen(o => !o)}
                >
                  <span>📅 Move to day</span>
                  <span style={{ fontSize: 10, opacity: 0.5 }}>{moveMenuOpen ? '▲' : '▼'}</span>
                </button>

                {moveMenuOpen && (
                  <div style={{ paddingLeft: 10, paddingBottom: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {displayDays.map((day, di) => (
                      di !== selected.dayIdx ? (
                        <button
                          key={di}
                          style={{ ...BTN, fontSize: 11, color: '#54504A', paddingLeft: 8 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f4efe6'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          onClick={() => { onMoveToDay?.(selected.id, di); setMoveMenuOpen(false); }}
                        >
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: DAY_COLORS[di % DAY_COLORS.length], marginRight: 6, verticalAlign: 'middle' }} />
                          Day {di + 1} · {day.date}
                        </button>
                      ) : null
                    ))}
                    {!selected.isParking && (
                      <button
                        style={{ ...BTN, fontSize: 11, color: '#54504A', paddingLeft: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f4efe6'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        onClick={() => { onMoveToParking?.(selected.id); setMoveMenuOpen(false); }}
                      >
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: PARKING_COLOR, marginRight: 6, verticalAlign: 'middle' }} />
                        Parking lot
                      </button>
                    )}
                    {selected.isParking && displayDays.length > 0 && (
                      <div style={{ fontSize: 10, color: '#9CA3AF', padding: '2px 8px', fontFamily: 'JetBrains Mono', fontStyle: 'italic' }}>
                        Pick a day above ↑
                      </div>
                    )}
                  </div>
                )}

                <button
                  style={{ ...BTN, color: '#C2402A' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f1d6cd'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => { onRemove?.(selected.id); setSelected(null); }}
                >
                  🗑 Remove from trip
                </button>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Legend */}
      <div style={{
        position: 'absolute', bottom: 24, right: 12, zIndex: 10,
        background: 'rgba(244,239,230,0.96)', backdropFilter: 'blur(8px)',
        border: `0.5px solid ${PALETTE.rule}`, borderRadius: 12,
        padding: '10px 14px', maxHeight: 220, overflowY: 'auto',
        boxShadow: '0 2px 10px rgba(34,31,26,0.1)',
      }}>
        {displayDays.map((day, di) => (
          day.items.length > 0 ? (
            <div key={di} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: 999, background: DAY_COLORS[di % DAY_COLORS.length], flexShrink: 0 }} />
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkSoft, whiteSpace: 'nowrap' }}>
                Day {di + 1} · {day.date}
              </span>
            </div>
          ) : null
        ))}
        {displayOverflow.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
            <div style={{ width: 10, height: 10, borderRadius: 999, background: PARKING_COLOR, flexShrink: 0 }} />
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkSoft }}>Parking lot</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function MapView({ displayDays, displayOverflow, allIdeas, destination, timeOverrides, accent, onEdit, onMoveToDay, onMoveToParking, onRemove, onClose }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  if (loadError) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: PALETTE.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 20, marginBottom: 10 }}>Map failed to load</div>
          <button onClick={onClose} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: PALETTE.card, padding: '8px 20px', borderRadius: 999, cursor: 'pointer', fontFamily: 'Inter', fontSize: 14 }}>Close</button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: PALETTE.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'Inter', fontSize: 14, color: PALETTE.inkMute }}>Loading map…</div>
      </div>
    );
  }

  return (
    <MapContent
      displayDays={displayDays}
      displayOverflow={displayOverflow}
      allIdeas={allIdeas}
      destination={destination}
      timeOverrides={timeOverrides}
      onEdit={onEdit}
      onMoveToDay={onMoveToDay}
      onMoveToParking={onMoveToParking}
      onRemove={onRemove}
      onClose={onClose}
    />
  );
}
